import { Loader2, Sparkles } from 'lucide-react';
import type { BootstrapProgress } from 'src/api/queries/getAppBootstrap';
import { cn } from 'src/lib/utils';

// ----------------------------------------------------------------------

interface BootstrapProgressBannerProps {
  bootstrapProgress: BootstrapProgress | null | undefined;
  className?: string;
}

/**
 * BUILDING-zero-state banner shown while the Tier 1 promotion lane is
 * still refining forecasts after the initial install sync completes.
 *
 * Composes alongside ``InitialSyncBanner`` — does NOT replace it. The
 * two banners cover different install phases:
 * - ``InitialSyncBanner``: phase === 'new' (bulk import not yet
 *   complete).
 * - ``BootstrapProgressBanner``: phase === 'building' (bulk import
 *   complete, Tier 1 fits in progress).
 *
 * Auto-hides at phase === 'ready' or when ``bootstrapProgress`` is
 * missing. Not user-dismissable — the band is small and reappears on
 * each session until READY.
 */
export function BootstrapProgressBanner({
  bootstrapProgress,
  className,
}: BootstrapProgressBannerProps) {
  if (!bootstrapProgress || bootstrapProgress.phase !== 'building') {
    return null;
  }

  const cursor = bootstrapProgress.cursor ?? 0;
  const total = bootstrapProgress.total ?? 0;
  const percentage = bootstrapProgress.percentage ?? 0;

  const description =
    total > 0
      ? `Setting up your forecasts — ${cursor} of ${total} products refined.`
      : 'Setting up your forecasts.';

  return (
    <div
      className={cn(
        'relative flex items-center gap-4 overflow-hidden border-b border-info/20 bg-info/5 px-5 py-4',
        className
      )}
      role="status"
      aria-live="polite"
    >
      {/* Decorative gradient accent */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-0.5 bg-gradient-to-b from-info/30 via-info/50 to-info/30" />

      <BootstrapProgressIcon />

      <div className="flex flex-1 flex-col gap-0.5">
        <p className="text-sm font-medium text-foreground">Refining your forecasts</p>
        <p className="text-xs text-muted-foreground">{description}</p>
        {total > 0 && (
          <div className="mt-1.5 flex items-center gap-3">
            <div
              className="h-1 flex-1 overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-valuenow={percentage}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="h-full bg-info transition-[width] duration-500 ease-out"
                style={{ width: `${Math.max(0, Math.min(100, percentage))}%` }}
              />
            </div>
            <span className="text-xs tabular-nums text-muted-foreground">{percentage}%</span>
          </div>
        )}
      </div>
    </div>
  );
}

function BootstrapProgressIcon() {
  return (
    <div className="relative flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
      <Sparkles className="h-5 w-5 text-muted-foreground" />
      <div className="absolute -right-0.5 -top-0.5">
        <div className="flex h-4 w-4 items-center justify-center rounded-full bg-background shadow-sm ring-1 ring-border">
          <Loader2 className="h-2.5 w-2.5 animate-spin text-foreground" />
        </div>
      </div>
    </div>
  );
}
